import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Axios from "axios";
import cn from "classnames";
import { v4 as uuid } from "uuid";
import {
    Button,
    ButtonToolbar,
    Container,
    Header,
    Content,
    Heading,
    Message,
    toaster,
} from "rsuite";

import InfoOutlineIcon from "@rsuite/icons/InfoOutline";

import constants from "utils/constants";
import {
    ChangeEntityFrameworkSettingToggleValue,
    ITenantDeleteWorkflow,
    TenantFeatureSettingWorkFlowToggleValue,
    TenantFeatureSettingWorkFlowType,
} from "utils/tenant-workflows/tenant-workflows-types";

import { isOn } from "utils/tenant-workflows/tenant-workflows-helper";

import useUrlToPathConversion from "hooks/useUrlToPathConvertation";

import { updateCultureSettings } from "api/environments/tenants/culture-settings-api";
import {
    toggleTenantFeatureSettings,
    updateCefToggles,
} from "api/environments/tenants/tenant-feature-settings-api";
import { updateFeatureConfigurations } from "api/environments/tenants/tenant-feature-configurations-api";
import { updateOcrConfigurations } from "api/environments/tenants/ocr-configurations-api";
import { updateDataImports } from "api/environments/tenants/data-import-api";
import { getErrorMessage } from "api/defaults";

import {
    EnrollStatus,
    ErpIntegrationData,
    EnrollCache,
    EnrollStep,
    EnrollWorkflow,
    StepApproveDataTypes,
    StepStatus,
    StepType,
    InitializeSettingsAndFeaturesData,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-types";
import { CultureSettings } from "store/environments/tenants/culture-settings/culture-settings-types";
import { DeletionType } from "store/environments/tenants/tenants-types";

import { IModalCallback } from "store/common/unsaved-data/unsaved-data-types";
import { DataImport } from "store/environments/tenants/data-import/data-import-types";
import { Features } from "store/environments/tenants/tenant-configurations/tenant-configurations-types";

import ProgressBar from "components/Environments/Tenants/EnrollTenant/ProgressBar/ProgressBar";
import StepsNavigation from "components/Environments/Tenants/EnrollTenant/StepsNavigation/StepsNavigation";
import SuccessMessageContainer from "components/Environments/Tenants/EnrollTenant/SuccessMessage/SuccessMessageContainer";
import StepFrameContainer from "components/Environments/Tenants/EnrollTenant/StepFrame/StepFrameContainer";
import ErrorSectionContainer from "components/Common/ErrorSection/ErrorSectionContainer";
import DeletingTenantPage from "components/Environments/Tenants/DeleteTenant/DeleteTenantPage/DeletingTenantPage";

import { featureSettings } from "../LeftMenu/FeatureConfigurations/Form/definitions";

import styles from "./EnrollTenant.module.css";

interface IComponentProps {
    enrollWorkflow: EnrollWorkflow | null;
    enrollSteps: EnrollStep[] | null;
    runningStepIndex: number;
    activeStep: EnrollStep | null;
    activeEnvironmentMoniker: string;
    activeEnvironmentId: string;
    orchestratorInstanceId: string | null;
    enrollCache: EnrollCache | null;
    errorMessage: string;
    errorEnrollManualStepResponseMessage: string;
    isUnsavedChangeAvailable: boolean;
    clearWorkflowData: () => void;
    setActiveStep: (activeStep: EnrollStep | null) => void;
    setErrorMessageEnrollStep: (message: string) => void;
    fetchEnrollWorkflow: (
        environmentMoniker: string,
        orchestratorInstanceId: string
    ) => void;
    startEnroll: (environmentMoniker: string) => void;
    terminateEnroll: (
        environmentMoniker: string,
        orchestratorInstanceId: string
    ) => void;
    setOrchestratorInstanceId: (orchestratorInstanceId: string | null) => void;
    approveEnrollStep: (
        environmentMoniker: string,
        orchestratorInstanceId: string,
        eventName: string,
        data: StepApproveDataTypes
    ) => void;
    retryEnrollStep: (
        environmentMoniker: string,
        orchestratorInstanceId: string,
        data: StepApproveDataTypes
    ) => void;
    continueEnrollStep: (
        environmentMoniker: string,
        orchestratorInstanceId: string,
        data: StepApproveDataTypes
    ) => void;
    fetchEnvironments: (environmentId?: string) => void;
    setIsUnsavedChangeAvailable: (isUnsavedChangeAvailable: boolean) => void;
    clearSecretActiveStepData: (data: ErpIntegrationData) => void;
    setErrorEnrollManualStepResponseMessage: (message: string) => void;
    setTenantDeleteWorkflow: (workflow: ITenantDeleteWorkflow | null) => void;
    setModalConfirmCallback: (modalConfirmCallback: IModalCallback) => void;
    setRefreshActivated: (isRefreshActivated: boolean) => void;
    setErrorMessageInactiveTenant: (message: string) => void;
}

const EnrollTenant: React.FC<IComponentProps> = ({
    enrollWorkflow,
    enrollSteps,
    runningStepIndex,
    activeStep,
    enrollCache,
    activeEnvironmentMoniker,
    activeEnvironmentId,
    orchestratorInstanceId,
    errorMessage,
    errorEnrollManualStepResponseMessage,
    isUnsavedChangeAvailable,
    clearWorkflowData,
    setActiveStep,
    setErrorMessageEnrollStep,
    fetchEnrollWorkflow,
    startEnroll,
    terminateEnroll,
    setOrchestratorInstanceId,
    approveEnrollStep,
    retryEnrollStep,
    continueEnrollStep,
    fetchEnvironments,
    setIsUnsavedChangeAvailable,
    clearSecretActiveStepData,
    setErrorEnrollManualStepResponseMessage,
    setTenantDeleteWorkflow,
    setModalConfirmCallback,
    setRefreshActivated,
    setErrorMessageInactiveTenant,
}: IComponentProps) => {
    const { t } = useTranslation();

    const { environmentId } = useParams<{ environmentId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const urlParams = new URLSearchParams(location.search);
    const instanceIdParameterName = "instanceId";
    const deletedTenantPathSegment = "deleted-tenant";
    const { pathname } = useLocation();
    const { params } = useUrlToPathConversion(pathname);
    const instanceIdParameterValue =
        params.instanceId || urlParams.get(instanceIdParameterName);

    const [isStepFailed, setIsStepFailed] = useState<boolean>(false);

    const activeStepRef = useRef<EnrollStep | null>();

    const [isActiveStepApproved, setIsActiveStepApproved] =
        useState<boolean>(false);
    const [isActiveStepRetried, setIsActiveStepRetried] =
        useState<boolean>(false);

    const [isStepFormValid, setIsStepFormValid] = useState(() => () => true);
    const [isDeletingMessageVisible, setIsDeletingMessageVisible] =
        useState(false);

    const isEnrollStarted = useRef(false);

    const currentStepIndex = activeStep?.order || 0;

    const isPendingAutomatedStep =
        activeStep !== null
            ? [constants.enroll.STEP_5].includes(activeStep.order)
            : false;

    const isManualStep = activeStep?.type === StepType.Manual;

    const isButtonToolbarVisible =
        (isManualStep && !(currentStepIndex < runningStepIndex)) ||
        isStepFailed;

    const isAutomatedStepInfoVisible =
        activeStep?.type === StepType.Automated &&
        !isPendingAutomatedStep &&
        !(currentStepIndex < runningStepIndex);

    const title = t("enrollTenant.pageTitle");

    const shouldEnablePrimaryButtonByActiveStepData = () => {
        if (errorEnrollManualStepResponseMessage.length > 0) {
            return false;
        }

        if (activeStep?.order !== constants.enroll.STEP_5) {
            return true;
        }

        switch (activeStep.status) {
            case StepStatus.NotStarted:
            case StepStatus.Running:
            case StepStatus.Success:
                return false;
            case StepStatus.Failed:
                return true;
            case StepStatus.Pending:
                return activeStep.isFinalized === true;
        }
    };

    const submitActiveStep = async (
        environmentMoniker: string,
        environmentId: string,
        orchestratorInstanceId: string,
        activeStep: EnrollStep
    ) => {
        try {
            if (
                activeStep.order === constants.enroll.STEP_5 ||
                activeStep.order === constants.enroll.STEP_10
            ) {
                setIsActiveStepApproved(true);
                await approveEnrollStep(
                    environmentMoniker,
                    orchestratorInstanceId,
                    activeStep.name,
                    {} as StepApproveDataTypes
                );
            } else if (
                isStepFormValid() ||
                activeStep.order === constants.enroll.STEP_2 ||
                activeStep.order === constants.enroll.STEP_14
            ) {
                setIsActiveStepApproved(true);

                const data = JSON.parse(
                    activeStep.data ?? ""
                ) as StepApproveDataTypes;

                if (data !== null) {
                    if (activeStep.order === constants.enroll.STEP_3) {
                        clearSecretActiveStepData(data as ErpIntegrationData);
                    }

                    if (
                        activeStep.order === constants.enroll.STEP_12 &&
                        enrollCache?.tenantId
                    ) {
                        const dataImports = data as DataImport[];

                        await updateDataImports(
                            environmentMoniker,
                            enrollCache.tenantId,
                            dataImports
                        );

                        const cefToggleValues = dataImports
                            .filter((dataImport) => isOn(dataImport.cef))
                            .map(
                                (dataImport) =>
                                    ({
                                        settingName: dataImport.entity,
                                        settingToggleValue: isOn(
                                            dataImport.cef
                                        ),
                                        axImportToggleValue: isOn(
                                            dataImport.axImport
                                        ),
                                    }) as ChangeEntityFrameworkSettingToggleValue
                            );

                        if (cefToggleValues.length !== 0) {
                            await updateCefToggles(
                                environmentMoniker,
                                enrollCache.tenantMoniker!,
                                cefToggleValues
                            );
                        }
                    }

                    if (
                        activeStep.order === constants.enroll.STEP_13 &&
                        enrollCache?.tenantId
                    ) {
                        const cultureSettings = (
                            data as InitializeSettingsAndFeaturesData
                        ).cultureSettings;

                        await submitCultureSettings(
                            environmentId,
                            enrollCache.tenantId,
                            cultureSettings
                        );

                        const ocrConfiguration = (
                            data as InitializeSettingsAndFeaturesData
                        ).ocrConfiguration;

                        await updateOcrConfigurations(
                            environmentId,
                            enrollCache.tenantId,
                            ocrConfiguration
                        );

                        const features = (
                            data as InitializeSettingsAndFeaturesData
                        ).features;

                        await updateFeatureConfigurations(
                            environmentMoniker,
                            enrollCache.tenantId,
                            features
                        );

                        const settings: TenantFeatureSettingWorkFlowToggleValue[] =
                            Object.entries(features)
                                .filter(([k]) =>
                                    featureSettings.has(k as keyof Features)
                                )
                                .map(([k, v]) => {
                                    const type = featureSettings.get(
                                        k as keyof Features
                                    ) as TenantFeatureSettingWorkFlowType;

                                    return {
                                        settingName:
                                            TenantFeatureSettingWorkFlowType[
                                                type
                                            ],
                                        toggleValue: v as boolean,
                                        data: null,
                                    };
                                });

                        await toggleTenantFeatureSettings(
                            environmentMoniker,
                            enrollCache.tenantMoniker!,
                            settings
                        );
                    }

                    await approveEnrollStep(
                        environmentMoniker,
                        orchestratorInstanceId,
                        activeStep.name,
                        data
                    );
                    setIsUnsavedChangeAvailable(false);
                }
            }
        } catch {
            return;
        }
    };

    const retryActiveStep = (
        environmentMoniker: string,
        orchestratorInstanceId: string
    ) => {
        retryEnrollStep(environmentMoniker, orchestratorInstanceId, true);
        setIsActiveStepRetried(true);
    };

    const continueCurrentStep = (
        environmentMoniker: string,
        orchestratorInstanceId: string
    ) => {
        continueEnrollStep(environmentMoniker, orchestratorInstanceId, true);
    };

    const handleStepSubmitClick = () => {
        if (
            enrollSteps &&
            activeEnvironmentMoniker &&
            orchestratorInstanceId &&
            activeStep
        ) {
            if (isStepFailed) {
                setIsActiveStepApproved(false);
                retryActiveStep(
                    activeEnvironmentMoniker,
                    orchestratorInstanceId
                );

                return;
            }

            if (isPendingAutomatedStep) {
                setIsActiveStepApproved(true);
                continueCurrentStep(
                    activeEnvironmentMoniker,
                    orchestratorInstanceId
                );

                return;
            }

            submitActiveStep(
                activeEnvironmentMoniker,
                activeEnvironmentId,
                orchestratorInstanceId,
                activeStep
            ).then();
        }
    };

    useEffect(() => {
        if (
            enrollWorkflow &&
            enrollWorkflow.status === EnrollStatus.Terminating
        ) {
            setIsDeletingMessageVisible(true);
        }
    }, [enrollWorkflow?.status]);

    useEffect(() => {
        if (!activeStep) {
            setActiveStep(enrollSteps && enrollSteps[runningStepIndex]);
        }
    });

    useEffect(() => {
        const isFailed = activeStep?.status === StepStatus.Failed;
        setIsStepFailed(isFailed);
        if (isFailed) {
            setErrorMessageEnrollStep(
                activeStep?.logs ?? t("error.defaultError")
            );
        } else {
            setErrorMessageEnrollStep("");
        }
    }, [activeStep, setErrorMessageEnrollStep, t]);

    useEffect(() => {
        if (instanceIdParameterValue) {
            setOrchestratorInstanceId(instanceIdParameterValue);
        } else if (orchestratorInstanceId) {
            urlParams.set(instanceIdParameterName, orchestratorInstanceId);
            navigate(`?${urlParams.toString()}`, { replace: true });
        }
    }, [
        orchestratorInstanceId,
        instanceIdParameterValue,
        setOrchestratorInstanceId,
        navigate,
        urlParams,
    ]);

    useEffect(() => {
        if (
            activeEnvironmentMoniker &&
            !orchestratorInstanceId &&
            !instanceIdParameterValue &&
            !isEnrollStarted.current
        ) {
            isEnrollStarted.current = true;
            startEnroll(activeEnvironmentMoniker);
        }
    }, [
        activeEnvironmentMoniker,
        startEnroll,
        instanceIdParameterValue,
        orchestratorInstanceId,
    ]);

    useEffect(() => {
        if (activeEnvironmentMoniker && orchestratorInstanceId) {
            fetchEnrollWorkflow(
                activeEnvironmentMoniker,
                orchestratorInstanceId
            );
        }

        return () => {
            setErrorEnrollManualStepResponseMessage("");
            clearWorkflowData();
        };
    }, [
        activeEnvironmentMoniker,
        orchestratorInstanceId,
        fetchEnrollWorkflow,
        clearWorkflowData,
    ]);

    useEffect(() => {
        return () => {
            if (instanceIdParameterValue !== orchestratorInstanceId) {
                setOrchestratorInstanceId(null);
            }
        };
    }, [
        instanceIdParameterValue,
        orchestratorInstanceId,
        setOrchestratorInstanceId,
    ]);

    useEffect(() => {
        activeStepRef.current = activeStep;
    }, [activeStep]);

    useEffect(() => {
        return () => {
            if (
                activeEnvironmentMoniker &&
                orchestratorInstanceId &&
                activeStepRef.current?.order === constants.enroll.STEP_1 &&
                activeStepRef.current.status === StepStatus.Running
            ) {
                terminateEnroll(
                    activeEnvironmentMoniker,
                    orchestratorInstanceId
                );
            }
        };
    }, [activeEnvironmentMoniker, orchestratorInstanceId, terminateEnroll]);

    useEffect(() => {
        if (!activeEnvironmentMoniker) {
            fetchEnvironments(environmentId);
        }
    }, [activeEnvironmentMoniker, environmentId, fetchEnvironments]);

    useEffect(() => {
        if (runningStepIndex >= 0) {
            setIsActiveStepApproved(false);
            setIsActiveStepRetried(false);
        }
    }, [runningStepIndex]);

    useEffect(() => {
        setIsActiveStepApproved(false);
        setIsActiveStepRetried(false);
    }, [activeStep?.status]);

    useEffect(() => {
        return () => {
            toaster.clear();
        };
    }, []);

    useEffect(() => {
        setErrorMessageInactiveTenant("");
    }, []);

    const submitCultureSettings = async (
        activeEnvironmentId: string,
        activeTenantId: string,
        cultureSettings: CultureSettings
    ) => {
        try {
            const [payload, status] = await updateCultureSettings(
                activeEnvironmentId,
                activeTenantId,
                cultureSettings
            );
            if (
                status === constants.statusCode.OK ||
                status === constants.statusCode.NoContent
            ) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    useEffect(() => {
        return () => {
            if (!location.pathname.includes(deletedTenantPathSegment)) {
                setTenantDeleteWorkflow(null);
            }
        };
    }, [setTenantDeleteWorkflow, history]);

    const handleStepClick = (stepIndex: number) => {
        if (enrollWorkflow?.status === EnrollStatus.Terminating) {
            return;
        }

        const isIndexLessThanActive = stepIndex <= runningStepIndex;

        if (isIndexLessThanActive) {
            if (isUnsavedChangeAvailable) {
                setModalConfirmCallback({
                    id: uuid(),
                    isAutoDeleteResistant: false,
                    isPageRedirect: true,
                    callback: () => {
                        setIsUnsavedChangeAvailable(false);
                        setActiveStep(enrollSteps && enrollSteps[stepIndex]);
                    },
                });
                setRefreshActivated(true);

                return;
            }
            setActiveStep(enrollSteps && enrollSteps[stepIndex]);
        }
    };

    const handleCloseClick = () => {
        navigate(`/environments/${environmentId}/tenants`);
    };

    const getButtonName = () => {
        if (isStepFailed) {
            return t("enrollTenant.retryBtn");
        } else if (activeStep?.order === constants.enroll.STEP_5) {
            return t("enrollTenant.confirmBtn");
        } else if (activeStep?.order === constants.enroll.STEP_14) {
            return t("enrollTenant.completeBtn");
        } else {
            return t("enrollTenant.saveBtn");
        }
    };

    if (
        enrollSteps &&
        enrollSteps[enrollSteps.length - 1].status === StepStatus.Success
    ) {
        return <SuccessMessageContainer />;
    }

    if (errorMessage.length > 0) {
        return (
            <div className={styles.errorMessageContainer}>
                <ErrorSectionContainer />
            </div>
        );
    }

    return (
        <>
            {enrollSteps && (
                <Container>
                    <Header style={{ marginBottom: "1rem" }}>
                        <Heading level={1}>{title}</Heading>
                    </Header>
                    <Content>
                        <ProgressBar
                            enrollSteps={enrollSteps}
                            runningStepIndex={runningStepIndex}
                        />
                        <div className={styles.stepsContainer}>
                            <StepsNavigation
                                enrollWorkflow={enrollWorkflow}
                                enrollSteps={enrollSteps}
                                currentStepIndex={currentStepIndex}
                                onStepClick={(index) => handleStepClick(index)}
                            />
                            {isDeletingMessageVisible && (
                                <div className={styles.rightSection}>
                                    <DeletingTenantPage
                                        deletionType={
                                            DeletionType.EnrollDeletion
                                        }
                                    />
                                </div>
                            )}
                            {!isDeletingMessageVisible && (
                                <div className={styles.rightSection}>
                                    <StepFrameContainer
                                        activeStep={activeStep}
                                        setIsStepFormValid={setIsStepFormValid}
                                        isActiveStepApproved={
                                            isActiveStepApproved
                                        }
                                        isActiveStepRetried={
                                            isActiveStepRetried
                                        }
                                        runningStepIndex={runningStepIndex}
                                    />
                                    {isButtonToolbarVisible && (
                                        <ButtonToolbar
                                            hidden={isButtonToolbarVisible}
                                            className={styles.buttonsSection}
                                        >
                                            <Button
                                                type="submit"
                                                onClick={() =>
                                                    handleStepSubmitClick()
                                                }
                                                className={cn(
                                                    styles.generalBtn,
                                                    styles.saveBtn
                                                )}
                                                disabled={
                                                    !shouldEnablePrimaryButtonByActiveStepData()
                                                }
                                                loading={
                                                    isActiveStepRetried ||
                                                    isActiveStepApproved
                                                }
                                            >
                                                {getButtonName()}
                                            </Button>
                                            <Button
                                                onClick={() =>
                                                    handleCloseClick()
                                                }
                                                className={cn(
                                                    styles.generalBtn,
                                                    styles.cancelBtn
                                                )}
                                            >
                                                {activeStep?.order ===
                                                constants.enroll.STEP_1
                                                    ? t(
                                                          "enrollTenant.cancelBtn"
                                                      )
                                                    : t(
                                                          "enrollTenant.closeBtn"
                                                      )}
                                            </Button>
                                            {currentStepIndex !== 0 && (
                                                <div
                                                    className={
                                                        styles.infoAboutCancelBtn
                                                    }
                                                >
                                                    <InfoOutlineIcon
                                                        className={
                                                            styles.infoIcon
                                                        }
                                                    />
                                                    {t(
                                                        "enrollTenant.closeBtnInformation"
                                                    )}
                                                </div>
                                            )}
                                        </ButtonToolbar>
                                    )}
                                    {isAutomatedStepInfoVisible && (
                                        <Message
                                            showIcon
                                            type="info"
                                            className={styles.infoMessage}
                                        >
                                            {t(
                                                "enrollTenant.automaticStepInfo"
                                            )}
                                        </Message>
                                    )}
                                </div>
                            )}
                        </div>
                    </Content>
                </Container>
            )}
        </>
    );
};

export default EnrollTenant;
